import { execFileSync, spawn } from 'node:child_process'
import fs from 'node:fs'
import path from 'node:path'

import * as p from '@clack/prompts'
import type { InitCommandOptions } from '@core-engine/commands/runtime.js'
import { loadConfigFile } from '@core-engine/config/resolver.js'
import { writeConfigFile } from '@core-engine/config/serialize.js'
import type { ResolvedBetterstartConfig } from '@core-engine/config/types.js'
import { appendEnvVars } from '@core-engine/utils/env.js'
import {
  createNextAppCommand,
  detectPackageManager,
  runCommand
} from '@core-engine/utils/package-manager.js'
import { detectNextMajorVersion, detectProject } from '@next-adapter/config/detect.js'
import {
  DEFAULT_ADMIN_NAMESPACE,
  deriveMigrationsDir,
  deriveNamespacePaths,
  resolveAdminNamespace,
  validateAdminDashboardPath,
  validateAdminNamespace
} from '@next-adapter/config/namespace.js'
import { getDefaultConfig } from '@next-adapter/config.js'
import { promptDatabase } from '@next-adapter/init/prompts/database.js'
import { type PluginPromptResult, promptPlugins } from '@next-adapter/init/prompts/plugins.js'
import { type ProjectPromptResult, promptProject } from '@next-adapter/init/prompts/project.js'
import { scaffoldApiRoutes } from '@next-adapter/init/scaffolders/api-routes.js'
import { scaffoldAuth } from '@next-adapter/init/scaffolders/auth.js'
import { scaffoldBase } from '@next-adapter/init/scaffolders/base.js'
import { scaffoldBiome } from '@next-adapter/init/scaffolders/biome.js'
import { scaffoldComponents } from '@next-adapter/init/scaffolders/components.js'
import { scaffoldCoreSchemas } from '@next-adapter/init/scaffolders/core-schemas.js'
import { scaffoldDatabase } from '@next-adapter/init/scaffolders/database.js'
import {
  DRIZZLE_KIT_POSTGRES_DRIVER_DEP,
  getCliDependencySyncPlan,
  getDependencyPlan,
  hasDrizzleKitPostgresDriverDependency,
  installDependenciesAsync
} from '@next-adapter/init/scaffolders/dependencies.js'
import {
  type DevScriptPortResult,
  detectDevPort,
  ensureDefaultDevScriptPort
} from '@next-adapter/init/scaffolders/dev-server.js'
import { scaffoldEnv } from '@next-adapter/init/scaffolders/env.js'
import { scaffoldLayout } from '@next-adapter/init/scaffolders/layout.js'
import { scaffoldNextConfig } from '@next-adapter/init/scaffolders/next-config.js'
import { scaffoldTailwind } from '@next-adapter/init/scaffolders/tailwind.js'
import { scaffoldTsconfig } from '@next-adapter/init/scaffolders/tsconfig.js'
import { isEmailProviderIntegration } from '@next-adapter/integration-registry.js'
import {
  type CollectedIntegrationConfig,
  installIntegrations,
  parseIntegrationList
} from '@next-adapter/integration-runtime.js'
import { installPlugins, parsePluginList } from '@next-adapter/plugin-runtime.js'
import { runDrizzlePush } from '@next-adapter/utils/drizzle-push.js'
import pc from 'picocolors'
import { buildSeedScript } from './seed.js'

// biome-ignore lint/suspicious/noControlCharactersInRegex: needed to strip ANSI escape sequences from captured command output
const ANSI_ESCAPE_PATTERN = /\u001B\[[0-?]*[ -/]*[@-~]/g
const REMOVABLE_NAMESPACE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

function normalizeNamespaceForRemoval(value: unknown): string | undefined {
  if (typeof value !== 'string') {
    return
  }

  const segment = value.trim()
  return REMOVABLE_NAMESPACE_PATTERN.test(segment) ? segment : undefined
}

function readNamespaceFromConfigSource(source: string): string | undefined {
  const match = source.match(/\bnamespace\s*:\s*(['"`])([^'"`]+)\1/)
  return normalizeNamespaceForRemoval(match?.[2])
}

function resolveNonInteractiveProject(name: string | undefined): ProjectPromptResult {
  const projectName = name?.trim() || 'my-app'

  if (projectName !== '.' && !/^[a-z0-9_-]+$/i.test(projectName)) {
    throw new Error('Project name can only contain letters, numbers, hyphens, and underscores')
  }

  return { projectName, useSrcDir: false }
}

async function readExistingConfigNamespace(cwd: string): Promise<string | undefined> {
  const configPath = path.resolve(cwd, 'admin.config.ts')
  if (!fs.existsSync(configPath)) {
    return
  }

  const sourceNamespace = readNamespaceFromConfigSource(fs.readFileSync(configPath, 'utf-8'))
  if (sourceNamespace) {
    return sourceNamespace
  }

  try {
    const loadedConfig = await loadConfigFile(configPath)
    return normalizeNamespaceForRemoval(loadedConfig.frameworkConfig?.next?.namespace)
  } catch {
    return
  }
}

export async function resolveForceInitNamespaces(
  cwd: string,
  targetNamespace: string
): Promise<string[]> {
  const namespaces = new Set<string>([validateAdminNamespace(targetNamespace)])
  const hasConfig = fs.existsSync(path.resolve(cwd, 'admin.config.ts'))
  const existingNamespace = await readExistingConfigNamespace(cwd)

  if (existingNamespace) {
    namespaces.add(existingNamespace)
  } else if (hasConfig) {
    namespaces.add(DEFAULT_ADMIN_NAMESPACE)
  }

  return [...namespaces]
}

export function removeExistingAdminPaths(
  cwd: string,
  namespaces: string | Iterable<string>
): number {
  const segments = Array.from(
    new Set(
      (typeof namespaces === 'string' ? [namespaces] : [...namespaces]).flatMap((namespace) => {
        const segment = normalizeNamespaceForRemoval(namespace)
        return segment ? [segment] : []
      })
    )
  )
  const nukeDirs = [
    ...segments.flatMap((segment) => [segment, `app/(${segment})`, `src/app/(${segment})`]),
    '.betterstart'
  ]
  const nukeFiles = ['admin.config.ts', 'ADMIN.md', 'drizzle.config.ts']
  let removed = 0

  for (const dir of nukeDirs) {
    const fullPath = path.resolve(cwd, dir)
    if (fs.existsSync(fullPath)) {
      fs.rmSync(fullPath, { recursive: true, force: true })
      removed++
    }
  }

  for (const file of nukeFiles) {
    const fullPath = path.resolve(cwd, file)
    if (fs.existsSync(fullPath)) {
      fs.unlinkSync(fullPath)
      removed++
    }
  }

  return removed
}

export async function runInitCommand(
  name: string | undefined,
  options: InitCommandOptions
): Promise<void> {
  p.intro(pc.bgCyan(pc.black(' Setup Your Dashboard ')))

  let cwd = process.cwd()
  let forceMode = Boolean(options.force)
  let namespace = DEFAULT_ADMIN_NAMESPACE
  if (options.namespace) {
    try {
      namespace = validateAdminNamespace(options.namespace)
    } catch (error) {
      p.log.error(error instanceof Error ? error.message : String(error))
      process.exit(1)
    }
  }

  if (!options.yes && !options.namespace) {
    const defaultDashboardPath = resolveAdminNamespace(DEFAULT_ADMIN_NAMESPACE).routePath
    const namespaceInput = await p.text({
      message: "What's the path you want the dashboard to be?",
      placeholder: `eg. ${defaultDashboardPath}`,
      defaultValue: defaultDashboardPath,
      validate(value) {
        try {
          validateAdminDashboardPath(value ?? defaultDashboardPath)
          return
        } catch (error) {
          return error instanceof Error ? error.message : String(error)
        }
      }
    })
    if (p.isCancel(namespaceInput)) {
      p.cancel('Setup cancelled.')
      process.exit(0)
    }
    namespace = validateAdminDashboardPath(namespaceInput)
  }

  let project = detectProject(cwd, namespace)
  let pm = detectPackageManager(cwd)
  let isFreshProject = false

  // Determine srcDir
  let srcDir: boolean
  if (project.isExisting) {
    p.log.info(`Next.js app detected ${pc.dim('·')} ${pc.cyan(pm)}`)
    srcDir = project.hasSrcDir

    if (!project.hasTypeScript) {
      p.log.error('TypeScript is required. Please add a tsconfig.json first.')
      process.exit(1)
    }

    if (forceMode) {
      const nuked = removeExistingAdminPaths(cwd, await resolveForceInitNamespaces(cwd, namespace))
      if (nuked > 0) {
        p.log.warn(`${pc.yellow('Force mode:')} removed ${nuked} existing admin paths`)
      }
      // Re-detect project after wipe
      project = detectProject(cwd, namespace)
    } else if (project.conflicts.length > 0) {
      const conflictLines = project.conflicts.map((c) => `${pc.yellow('▲')} ${c}`)
      conflictLines.push(
        '',
        pc.dim(`Use ${pc.bold('--force')} to remove existing admin files before scaffolding.`)
      )
      p.note(conflictLines.join('\n'), pc.yellow('Conflicts'))
      if (!options.yes) {
        const proceed = await p.confirm({
          message: [
            `Continue with ${pc.bold(pc.cyan('--force'))}?`,
            `${pc.cyan('│')}  ${pc.dim('This will force overwrite the existing admin code.')}`,
            pc.cyan('│')
          ].join('\n'),
          initialValue: true
        })
        if (p.isCancel(proceed) || !proceed) {
          p.cancel('Setup cancelled.')
          process.exit(0)
        }

        forceMode = true
        const nuked = removeExistingAdminPaths(
          cwd,
          await resolveForceInitNamespaces(cwd, namespace)
        )
        if (nuked > 0) {
          p.log.warn(`${pc.yellow('Force mode:')} removed ${nuked} existing admin paths`)
        }
        project = detectProject(cwd, namespace)
      }
    }
  } else {
    p.log.info('No Next.js app found — Running the fresh project mode...')
    let projectPrompt: ProjectPromptResult
    try {
      projectPrompt = options.yes ? resolveNonInteractiveProject(name) : await promptProject(name)
    } catch (error) {
      p.log.error(error instanceof Error ? error.message : String(error))
      process.exit(1)
    }
    srcDir = projectPrompt.useSrcDir

    // Ask for preferred package manager
    if (!options.yes) {
      const pmChoice = await p.select({
        message: 'Which package manager do you want to use?',
        options: [
          { value: 'pnpm' as const, label: 'pnpm', hint: 'recommended' },
          { value: 'npm' as const, label: 'npm' },
          { value: 'yarn' as const, label: 'yarn' },
          { value: 'bun' as const, label: 'bun' }
        ]
      })
      if (p.isCancel(pmChoice)) {
        p.cancel('Setup cancelled.')
        process.exit(0)
      }
      pm = pmChoice
    }

    // Run create-next-app using the selected package manager's native command
    // to avoid nested-npx issues when BetterStart shells out to CNA.
    const displayName =
      projectPrompt.projectName === '.' ? path.basename(cwd) : projectPrompt.projectName

    const { bin, prefix } = createNextAppCommand(pm)
    const cnaArgs = [
      ...prefix,
      projectPrompt.projectName,
      '--yes',
      '--typescript',
      '--tailwind',
      '--app',
      '--turbopack',
      '--biome',
      '--react-compiler',
      `--use-${pm}`
    ]
    if (srcDir) cnaArgs.push('--src-dir')
    else cnaArgs.push('--no-src-dir')

    const createNextAppSpinner = p.spinner()
    createNextAppSpinner.start(`Creating a Next.js app (latest)`)

    const createNextAppResult = await runQuietCommand(bin, cnaArgs, {
      cwd,
      timeoutMs: 120_000
    })

    if (!createNextAppResult.success) {
      createNextAppSpinner.stop(`Failed to create Next.js app: ${displayName}`)
      if (createNextAppResult.output.trim().length > 0) {
        process.stderr.write(`${createNextAppResult.output.trimEnd()}\n`)
      }
      p.log.error(createNextAppResult.error)
      p.log.info(
        `You can create the project manually:\n  ${pc.cyan(`npx create-next-app@latest ${projectPrompt.projectName} --typescript --tailwind --app`)}\n  Then run ${pc.cyan('betterstart init')} inside it.`
      )
      process.exit(1)
    }

    // Move into the new project directory
    cwd = path.resolve(cwd, projectPrompt.projectName)

    // Verify the project was actually created
    const hasPackageJson = fs.existsSync(path.join(cwd, 'package.json'))
    const hasNextConfig = ['next.config.ts', 'next.config.js', 'next.config.mjs'].some((f) =>
      fs.existsSync(path.join(cwd, f))
    )

    if (!hasPackageJson || !hasNextConfig) {
      createNextAppSpinner.stop(`Failed to create Next.js app: ${displayName}`)
      p.log.error(
        'create-next-app completed but the project was not created. This can happen with nested npx calls.'
      )
      const manualCmd = `npx create-next-app@latest ${projectPrompt.projectName} --typescript --tailwind --app`
      p.log.info(
        `Create the project manually:\n  ${pc.cyan(manualCmd)}\n  Then run ${pc.cyan('betterstart init')} inside it.`
      )
      process.exit(1)
    }

    const installedNextVersion = detectInstalledNextVersion(cwd)
    const nextVersionSuffix = installedNextVersion ? `${pc.cyan(`v${installedNextVersion}`)}` : ''

    createNextAppSpinner.stop(`Created a Next.js ${nextVersionSuffix} app in ${displayName}`)
    project = detectProject(cwd, namespace)
    isFreshProject = true
  }

  const selectedPlugins = (() => {
    if (options.plugins) {
      return parsePluginList(options.plugins)
    }

    return []
  })()
  const selectedIntegrations = (() => {
    if (options.integrations) {
      return parseIntegrationList(options.integrations)
    }

    return []
  })()

  // Integration secrets (Resend, R2) are collected inline by promptPlugins, right
  // after each provider is selected, so input happens up front — before scaffolding
  // — and the install step below never stops to prompt mid-scaffold. The collected
  // values are written to .env.local once it exists; installIntegrations then runs
  // non-interactively.
  let collectedIntegrationConfig: CollectedIntegrationConfig = {
    sections: [],
    overwriteKeys: new Set<string>()
  }
  let pluginSelection: Omit<PluginPromptResult, 'integrationConfig'>

  if (selectedPlugins.length > 0 || selectedIntegrations.length > 0) {
    pluginSelection = {
      plugins: selectedPlugins,
      integrations: selectedIntegrations,
      storage: selectedIntegrations.includes('r2') ? 'r2' : 'local'
    }
  } else if (options.yes) {
    pluginSelection = { plugins: [], integrations: [], storage: 'local' }
  } else {
    const promptResult = await promptPlugins(cwd)
    collectedIntegrationConfig = promptResult.integrationConfig
    pluginSelection = {
      plugins: promptResult.plugins,
      integrations: promptResult.integrations,
      storage: promptResult.storage
    }
  }

  // Database URL — check existing .env.local first, then prompt if needed
  let databaseUrl: string | undefined
  const existingDbUrl = readExistingDbUrl(cwd)

  if (options.yes) {
    // Non-interactive mode: --database-url flag > existing env > placeholder
    if (options.databaseUrl) {
      if (!isValidDbUrl(options.databaseUrl)) {
        p.log.error(
          `Invalid database URL. Must start with ${pc.cyan('postgres://')} or ${pc.cyan('postgresql://')}`
        )
        process.exit(1)
      }
      databaseUrl = options.databaseUrl
    } else if (existingDbUrl) {
      databaseUrl = existingDbUrl
    }
    // else: no flag, no existing → use placeholder (databaseUrl stays undefined)
  } else if (existingDbUrl) {
    // Interactive mode but a valid URL already exists — skip the prompt
    const masked = maskDbUrl(existingDbUrl)
    p.log.info(`Using existing database URL from .env.local ${pc.dim(`(${masked})`)}`)
    databaseUrl = existingDbUrl
  } else {
    const dbResult = await promptDatabase()
    databaseUrl = dbResult.url
  }

  // Build config
  const nextMajorVersion = detectNextMajorVersion(cwd)
  const config: ResolvedBetterstartConfig = {
    ...getDefaultConfig(srcDir, nextMajorVersion)
  }
  config.frameworkConfig.next.namespace = namespace
  config.frameworkConfig.next.paths = deriveNamespacePaths(namespace, srcDir)
  config.paths = config.frameworkConfig.next.paths
  config.database.migrationsDir = deriveMigrationsDir(namespace)

  // Run scaffolders — group fast steps under one spinner, then show results in a note box
  interface ScaffoldResult {
    label: string
    result: string
  }
  const results: ScaffoldResult[] = []
  const s = p.spinner()

  s.start('Directory structure')

  const baseFiles = scaffoldBase({
    cwd,
    config,
    includeEmailTemplatesDir: pluginSelection.integrations.some(isEmailProviderIntegration)
  })
  results.push({ label: 'Directory structure', result: `${baseFiles.length} files` })

  s.message('TypeScript aliases')
  const tsResult = scaffoldTsconfig(cwd, config)
  results.push({
    label: 'TypeScript aliases',
    result: tsResult.added.length > 0 ? `${tsResult.added.length} paths` : 'already set'
  })

  s.message('Tailwind CSS')
  const twResult = scaffoldTailwind(cwd, srcDir, namespace)
  results.push({
    label: 'Tailwind CSS',
    result: twResult.appended ? 'updated' : twResult.file ? 'already set' : 'no CSS file'
  })

  s.message('Dev server')
  const devServerResult = ensureDefaultDevScriptPort(cwd)
  results.push({
    label: 'Dev server',
    result: formatDevServerResult(devServerResult)
  })

  s.message('Environment variables')
  const envResult = scaffoldEnv(cwd, { databaseUrl, namespace })
  if (collectedIntegrationConfig.sections.length > 0) {
    appendEnvVars(
      cwd,
      collectedIntegrationConfig.sections,
      collectedIntegrationConfig.overwriteKeys
    )
  }
  const envCount = envResult.added.length + envResult.updated.length
  results.push({
    label: 'Environment variables',
    result: envCount > 0 ? `${envCount} vars` : 'already set'
  })

  s.message('Database')
  const dbFiles = scaffoldDatabase({ cwd, config })
  results.push({ label: 'Database', result: `${dbFiles.length} files` })

  s.message('Authentication')
  const authFiles = scaffoldAuth({ cwd, config })
  results.push({ label: 'Authentication', result: `${authFiles.length} files` })

  s.message('Components')
  const compFiles = scaffoldComponents({ cwd, config })
  results.push({ label: 'Components', result: `${compFiles.length} files` })

  s.message('Pages & layouts')
  const layoutFiles = scaffoldLayout({ cwd, config })
  results.push({ label: 'Pages & layouts', result: `${layoutFiles.length} files` })

  s.message('API routes')
  const apiFiles = scaffoldApiRoutes({ cwd, config })
  results.push({ label: 'API routes', result: `${apiFiles.length} routes` })

  s.message('Linter')
  let linterResult: string
  if (project.linter.type === 'none') {
    const biomeResult = scaffoldBiome(cwd, project.linter)
    linterResult = biomeResult.installed ? 'biome (new)' : 'none'
  } else {
    linterResult = project.linter.type
  }
  results.push({ label: 'Linter', result: linterResult })

  s.message('Next.js config')
  const nextConfigResult = scaffoldNextConfig({ cwd, nextMajorVersion })
  results.push({
    label: 'Next.js config',
    result:
      nextConfigResult.status === 'created'
        ? 'created'
        : nextConfigResult.status === 'updated'
          ? 'updated'
          : nextConfigResult.status === 'already-set'
            ? 'already set'
            : nextConfigResult.status === 'not-needed'
              ? 'not needed'
              : 'manual setup needed'
  })

  // Build the note content before stopping the spinner
  const maxLabel = Math.max(...results.map((r) => r.label.length))
  const noteLines = results.map((r) => {
    const padded = r.label.padEnd(maxLabel + 3)
    return `${pc.green('✓')} ${padded}${pc.dim(r.result)}`
  })

  // Stop spinner quietly and erase the empty stop line, then show results card
  s.stop('')
  process.stdout.write('\x1B[2A\x1B[J')
  p.note(noteLines.join('\n'), 'Scaffolded admin')

  // If drizzle.config.ts already existed (wasn't created), offer to update it
  const drizzleConfigPath = path.join(cwd, 'drizzle.config.ts')
  if (!dbFiles.includes('drizzle.config.ts') && fs.existsSync(drizzleConfigPath)) {
    if (forceMode) {
      const { readNamespacedTemplate } = await import('@next-adapter/template-reader.js')
      fs.writeFileSync(
        drizzleConfigPath,
        readNamespacedTemplate('drizzle.config.ts', namespace),
        'utf-8'
      )
      p.log.success('Updated drizzle.config.ts')
    } else if (!options.yes) {
      const overwrite = await p.confirm({
        message: 'drizzle.config.ts already exists. Overwrite with latest version?',
        initialValue: true
      })
      if (!p.isCancel(overwrite) && overwrite) {
        const { readNamespacedTemplate } = await import('@next-adapter/template-reader.js')
        fs.writeFileSync(
          drizzleConfigPath,
          readNamespacedTemplate('drizzle.config.ts', namespace),
          'utf-8'
        )
        p.log.success('Updated drizzle.config.ts')
      }
    }
  }

  // Install dependencies (async so spinner can animate)
  const coreDependencyPlan = getDependencyPlan([], [], project.linter.type === 'none')
  const cliDependencyPlan = getCliDependencySyncPlan(cwd)
  s.start('Installing dependencies (this may take a minute)')
  const depsResult = await installDependenciesAsync({
    cwd,
    pm,
    dependencies: Array.from(
      new Set([...coreDependencyPlan.dependencies, ...(cliDependencyPlan?.dependencies ?? [])])
    ),
    devDependencies: Array.from(
      new Set([
        ...coreDependencyPlan.devDependencies,
        ...(cliDependencyPlan?.devDependencies ?? [])
      ])
    )
  })
  let depsInstalled = false
  if (depsResult.success) {
    s.stop('')
    depsInstalled = true
  } else {
    s.stop('Failed to install dependencies')
    p.log.warning(depsResult.error ?? 'Unknown error')
    p.log.info(
      `You can install them manually:\n  ${pc.cyan(`${pm} add ${depsResult.dependencies.join(' ')}`)}\n  ${pc.cyan(`${pm} add -D ${depsResult.devDeps.join(' ')}`)}`
    )
  }

  // Generate core-owned schemas
  if (depsInstalled) {
    process.stdout.write('\x1B[2A\x1B[J')
  }
  s.start('Generating core schemas')
  const coreSchemasResult = scaffoldCoreSchemas({ cwd, config })
  s.stop('')
  process.stdout.write('\x1B[2A\x1B[J')

  // Install selected plugins through the shared plugin pipeline
  const pluginInstallResult =
    pluginSelection.plugins.length > 0
      ? (() => {
          s.start(`Installing plugins: ${pluginSelection.plugins.join(', ')}`)
          return installPlugins({
            cwd,
            config,
            pm,
            pluginIds: pluginSelection.plugins,
            interactive: !options.yes,
            includeBiome: project.linter.type === 'none'
          })
        })()
      : Promise.resolve({ installed: [], skipped: [], warnings: [], config })
  const resolvedPluginInstallResult = await pluginInstallResult
  if (pluginSelection.plugins.length > 0) {
    s.stop('')
    process.stdout.write('\x1B[2A\x1B[J')
  }

  const integrationInstallResult =
    pluginSelection.integrations.length > 0
      ? (() => {
          s.start(`Installing integrations: ${pluginSelection.integrations.join(', ')}`)
          return installIntegrations({
            cwd,
            config: resolvedPluginInstallResult.config,
            pm,
            integrationIds: pluginSelection.integrations,
            // Secrets were already collected up front and written to .env.local,
            // so the install runs without prompting (rule: no mid-scaffold input).
            interactive: false,
            includeBiome: project.linter.type === 'none'
          })
        })()
      : Promise.resolve({
          installed: [],
          skipped: [],
          warnings: [],
          config: resolvedPluginInstallResult.config
        })
  const resolvedIntegrationInstallResult = await integrationInstallResult
  if (pluginSelection.integrations.length > 0) {
    s.stop('')
    process.stdout.write('\x1B[2A\x1B[J')
  }

  writeConfigFile(cwd, resolvedIntegrationInstallResult.config)
  const usesLocalStorage = resolvedIntegrationInstallResult.config.storage.provider !== 'r2'

  // Build combined note for deps + core/plugin install
  const installLines: string[] = []
  if (depsInstalled) {
    installLines.push(
      `${pc.green('✓')} Dependencies        ${pc.dim(`${depsResult.dependencies.length} deps + ${depsResult.devDeps.length} dev deps`)}`
    )
  }
  if (coreSchemasResult.errors.length > 0) {
    installLines.push(
      `${pc.yellow('▲')} Core schemas         ${pc.dim(`${coreSchemasResult.errors.length} warning(s)`)}`
    )
    for (const err of coreSchemasResult.errors) {
      installLines.push(`  ${pc.dim(err)}`)
    }
  } else {
    installLines.push(
      `${pc.green('✓')} Core schemas         ${pc.dim(`${coreSchemasResult.schemas.length} schemas, ${coreSchemasResult.generatedFiles.length} files`)}`
    )
  }
  installLines.push(
    `${pc.green('✓')} Plugins             ${pc.dim(resolvedPluginInstallResult.installed.length > 0 ? resolvedPluginInstallResult.installed.join(', ') : 'core only')}`
  )
  for (const warning of resolvedPluginInstallResult.warnings) {
    installLines.push(`  ${pc.yellow('▲')} ${warning}`)
  }
  installLines.push(
    `${pc.green('✓')} Integrations        ${pc.dim(resolvedIntegrationInstallResult.installed.length > 0 ? resolvedIntegrationInstallResult.installed.join(', ') : 'none')}`
  )
  for (const warning of resolvedIntegrationInstallResult.warnings) {
    installLines.push(`  ${pc.yellow('▲')} ${warning}`)
  }
  if (usesLocalStorage) {
    installLines.push(
      `  ${pc.yellow('▲')} Local filesystem storage is only for development or self-hosted persistent-disk deployments. Use Cloudflare R2 or AWS S3 for hosted/serverless production.`
    )
  }
  p.note(installLines.join('\n'), 'Installed')

  // Push database schema after core/plugin generation so entity tables are included
  let dbPushed = false
  if (depsResult.success && options.skipMigration && hasDbUrl(cwd)) {
    p.log.info(`Skipping database schema push ${pc.dim('(--skip-migration)')}`)
  } else if (depsResult.success && hasDbUrl(cwd)) {
    let driverReady = hasDrizzleKitPostgresDriverDependency(cwd)

    if (!driverReady) {
      s.start(
        `Installing Drizzle Kit Postgres driver dependency (${DRIZZLE_KIT_POSTGRES_DRIVER_DEP})`
      )
      const driverResult = await installDependenciesAsync({
        cwd,
        pm,
        dependencies: [],
        devDependencies: [DRIZZLE_KIT_POSTGRES_DRIVER_DEP]
      })

      if (!driverResult.success) {
        s.stop('Database push failed')
        p.log.warning(driverResult.error ?? `Failed to install ${DRIZZLE_KIT_POSTGRES_DRIVER_DEP}`)
        p.log.info(
          `Install ${DRIZZLE_KIT_POSTGRES_DRIVER_DEP} manually, then run: ${pc.cyan(drizzlePushCommand(pm))}`
        )
      } else {
        driverReady = hasDrizzleKitPostgresDriverDependency(cwd)
        s.stop(`${pc.green('✓')} Drizzle Kit Postgres driver dependency installed`)
      }
    }

    if (driverReady) {
      const useTerminalForDbPush = shouldUseTerminalForDbPush(options)
      if (useTerminalForDbPush) {
        p.log.info(`Running ${pc.cyan('drizzle-kit push --force')}`)
      } else {
        s.start('Pushing database schema (drizzle-kit push)')
      }

      const pushResult = await runDrizzlePush(cwd, { inheritStdio: useTerminalForDbPush })
      if (pushResult.success) {
        if (useTerminalForDbPush) {
          const verification = await verifyDatabaseReachable(cwd)
          if (!verification.success) {
            p.log.warning(verification.error)
            p.log.error('Database was not reachable. Aborting setup.')
            process.exit(1)
          }
        }
        if (useTerminalForDbPush) {
          p.log.success('Database schema pushed')
        } else {
          s.stop(`${pc.green('✓')} Database schema pushed`)
        }
        dbPushed = true
      } else {
        if (!useTerminalForDbPush) {
          s.stop('Database push failed')
        }
        const pushError = pushResult.error ?? 'Unknown error'
        p.log.warning(pushError)
        if (isDatabaseReachabilityError(pushError)) {
          p.log.error('Database was not reachable. Aborting setup.')
          process.exit(1)
        }
        p.log.info(`You can run it manually: ${pc.cyan(drizzlePushCommand(pm))}`)
      }
    }
  }

  // Auto-seed admin user (only when db push succeeded and interactive mode)
  let seedEmail: string | undefined
  let seedPassword: string | undefined
  let seedSuccess = false
  let adminAccountReady = false

  if (dbPushed && options.skipAdminCreation) {
    p.log.info(
      `Skipping admin user creation ${pc.dim(`(use ${betterstartExecCommand(pm, 'seed')} later)`)}`
    )
  }

  if (dbPushed && !options.yes && !options.skipAdminCreation) {
    const adminDir = config.paths?.admin ?? './admin'
    const authBasePath = `${resolveAdminNamespace(namespace).apiPath}/auth`
    let replaceExistingAdmin = false
    const adminCheck = await checkExistingAdmin(cwd, adminDir, authBasePath)

    if (adminCheck.error) {
      p.log.warning(`Could not verify existing admin account ${pc.dim(`(${adminCheck.error})`)}`)
      if (isDatabaseReachabilityError(adminCheck.error)) {
        p.log.error('Database was not reachable. Aborting setup.')
        process.exit(1)
      }
    } else if (adminCheck.existingAdmin) {
      const existingAdminLabel = formatAdminIdentity(adminCheck.existingAdmin)
      const adminAction = await p.select({
        message: 'Found an already existing admin account. Do you want to replace it or skip?',
        options: [
          {
            value: 'replace' as const,
            label: 'Replace',
            hint: existingAdminLabel
          },
          {
            value: 'skip' as const,
            label: 'Skip'
          }
        ]
      })

      if (p.isCancel(adminAction)) {
        p.cancel('Setup cancelled.')
        process.exit(0)
      }

      if (adminAction === 'skip') {
        adminAccountReady = true
        p.log.info(`Keeping existing admin account ${pc.dim(`(${existingAdminLabel})`)}`)
      } else {
        replaceExistingAdmin = true
      }
    }

    if (!adminAccountReady) {
      p.note(
        pc.dim(
          replaceExistingAdmin
            ? 'Replace the existing admin account to access the admin.'
            : 'Create your first admin user to access the admin.'
        ),
        'Admin account'
      )

      const credentials = await p.group(
        {
          email: () =>
            p.text({
              message: 'Admin email',
              placeholder: 'admin@example.com',
              validate: (v) => {
                if (!v || !v.includes('@')) return 'Please enter a valid email'
              }
            }),
          password: () =>
            p.password({
              message: 'Admin password',
              validate: (v) => {
                if (!v || v.length < 8) return 'Password must be at least 8 characters'
              }
            })
        },
        {
          onCancel: () => {
            p.cancel('Setup cancelled.')
            process.exit(0)
          }
        }
      )

      seedEmail = credentials.email
      seedPassword = credentials.password

      let seedOverwriteMode: SeedOverwriteMode | undefined = replaceExistingAdmin
        ? 'admin'
        : undefined
      s.start(replaceExistingAdmin ? 'Replacing admin user' : 'Creating admin user')
      let seedResult = await runSeed(
        cwd,
        adminDir,
        authBasePath,
        credentials.email,
        credentials.password,
        seedOverwriteMode
      )

      if (seedResult.existingUser) {
        s.stop(`${pc.yellow('▲')} An account already exists for ${credentials.email}`)
        const replace = await p.confirm({
          message: 'Replace the existing account with this email?',
          initialValue: false
        })
        if (!p.isCancel(replace) && replace) {
          seedOverwriteMode = 'email'
          s.start('Replacing admin user')
          seedResult = await runSeed(
            cwd,
            adminDir,
            authBasePath,
            credentials.email,
            credentials.password,
            seedOverwriteMode
          )
        }
      }

      if (seedResult.success) {
        s.stop(
          seedOverwriteMode === 'admin'
            ? `${pc.green('✓')} Admin user replaced`
            : `${pc.green('✓')} Admin user created`
        )
        seedSuccess = true
        adminAccountReady = true
      } else if (seedResult.error) {
        s.stop(`${pc.red('✗')} Failed to create admin user`)
        p.note(
          `${pc.red(seedResult.error)}\n\nRun manually: ${pc.cyan(betterstartExecCommand(pm, 'seed'))}`,
          pc.red('Seed failed')
        )
        if (isDatabaseReachabilityError(seedResult.error)) {
          p.log.error('Database was not reachable. Aborting setup.')
          process.exit(1)
        }
      }
    }
  }

  // Git init + commit for fresh projects
  if (isFreshProject) {
    s.start('Creating initial git commit')
    try {
      execFileSync('git', ['init'], { cwd, stdio: 'pipe' })
      execFileSync('git', ['add', '.'], { cwd, stdio: 'pipe' })
      execFileSync('git', ['commit', '-m', 'Initial commit from BetterStart'], {
        cwd,
        stdio: 'pipe'
      })
      s.stop('Created initial git commit')
    } catch {
      s.stop('Git commit skipped')
    }
  }

  const totalFiles =
    baseFiles.length +
    dbFiles.length +
    authFiles.length +
    compFiles.length +
    layoutFiles.length +
    apiFiles.length

  const adminNamespace = resolveAdminNamespace(namespace)
  const adminLoginUrl = `http://localhost:${detectDevPort(cwd)}${adminNamespace.routePath}/login`
  const installedPluginList =
    resolvedPluginInstallResult.installed.length > 0
      ? resolvedPluginInstallResult.installed.join(', ')
      : 'core only'
  const installedIntegrationList =
    resolvedIntegrationInstallResult.installed.length > 0
      ? resolvedIntegrationInstallResult.installed.join(', ')
      : 'none'

  // Summary
  const summaryLines: string[] = [
    `Plugins: ${pc.cyan(installedPluginList)}`,
    `Integrations: ${pc.cyan(installedIntegrationList)}`,
    `Storage: ${pc.cyan(usesLocalStorage ? 'local filesystem' : 'Cloudflare R2')}`,
    `Files created: ${pc.cyan(String(totalFiles))}`,
    `Env vars: ${envResult.added.length} added, ${envResult.skipped.length} skipped`
  ]

  // Next steps
  const nextSteps: string[] = []
  let step = 1
  const envStepLabel = databaseUrl
    ? `Fill in remaining values in ${pc.cyan('.env.local')}`
    : `Fill in values in ${pc.cyan('.env.local')}`
  nextSteps.push(`  ${step++}. ${envStepLabel}`)
  if (!dbPushed) {
    nextSteps.push(`  ${step++}. Run ${pc.cyan(drizzlePushCommand(pm))} to sync the database`)
  }
  if (!adminAccountReady) {
    nextSteps.push(
      `  ${step++}. Run ${pc.cyan(betterstartExecCommand(pm, 'seed'))} to create an admin user`
    )
  }
  nextSteps.push(
    `  ${step++}. Run ${pc.cyan(runCommand(pm, 'dev'))} and open ${pc.cyan(adminLoginUrl)}`
  )
  nextSteps.push(
    `  ${step++}. Run ${pc.cyan(betterstartExecCommand(pm, 'generate <schema>'))} to create content types`
  )
  nextSteps.push(
    `  ${step++}. Run ${pc.cyan(betterstartExecCommand(pm, 'add --integration <name>'))} to install integrations later`
  )

  summaryLines.push('', 'Next steps:', ...nextSteps)

  p.note(summaryLines.join('\n'), 'Admin scaffolded successfully')

  // Offer to start the dev server (interactive mode only)
  if (!options.yes && !options.skipDevServerStart) {
    const devCmd = runCommand(pm, 'dev')
    const startDev = await p.confirm({
      message: 'Start the development server?',
      initialValue: true
    })

    if (!p.isCancel(startDev) && startDev) {
      p.outro(`Starting ${pc.cyan(devCmd)}...`)
      await startManagedDevServer(cwd, devCmd, adminLoginUrl, {
        email: seedSuccess && seedEmail ? seedEmail : undefined,
        password: seedSuccess && seedPassword ? seedPassword : undefined
      })
      return
    }
  }

  p.outro('Done!')
}

// ============================================================================
// Helpers
// ============================================================================

/** Validate a database connection URL */
function isValidDbUrl(url: string): boolean {
  return url.startsWith('postgres://') || url.startsWith('postgresql://')
}

/** Read DATABASE_URL from .env.local, returning the value if it's a real URL. */
function readExistingDbUrl(cwd: string): string | undefined {
  const envPath = path.join(cwd, '.env.local')
  if (!fs.existsSync(envPath)) return undefined
  const content = fs.readFileSync(envPath, 'utf-8')
  for (const line of content.split('\n')) {
    const trimmed = line.trim()
    if (trimmed.startsWith('#') || !trimmed.includes('=')) continue
    const [key, ...rest] = trimmed.split('=')
    if (key?.trim() === 'DATABASE_URL') {
      const val = rest
        .join('=')
        .replace(/^['"]|['"]$/g, '')
        .trim()
      if (
        val.length > 0 &&
        !val.startsWith('your_') &&
        val !== 'postgresql://...' &&
        isValidDbUrl(val)
      ) {
        return val
      }
    }
  }
  return undefined
}

/** Mask a database URL for display: show host only. */
function maskDbUrl(url: string): string {
  try {
    const parsed = new URL(url)
    return `${parsed.protocol}//${parsed.host}/***`
  } catch {
    return 'postgres://***'
  }
}

/** Check if DATABASE_URL is set in .env.local */
function hasDbUrl(cwd: string): boolean {
  const envPath = path.join(cwd, '.env.local')
  if (!fs.existsSync(envPath)) return false
  const content = fs.readFileSync(envPath, 'utf-8')
  // Check the var exists and has a non-placeholder value
  for (const line of content.split('\n')) {
    const trimmed = line.trim()
    if (trimmed.startsWith('#') || !trimmed.includes('=')) continue
    const [key, ...rest] = trimmed.split('=')
    if (key?.trim() === 'DATABASE_URL') {
      const val = rest.join('=').trim()
      const unquoted = val.replace(/^['"]|['"]$/g, '')
      return unquoted.length > 0 && !unquoted.startsWith('your_') && unquoted !== 'postgresql://...'
    }
  }
  return false
}

interface SeedResult {
  success: boolean
  error: string | null
  existingUser?: string
}

interface ExistingAdmin {
  email: string
  name?: string
}

interface ExistingAdminCheckResult {
  existingAdmin?: ExistingAdmin
  error: string | null
}

type SeedOverwriteMode = 'admin' | 'email'

interface QuietCommandResult {
  success: boolean
  error: string
  output: string
}

function detectInstalledNextVersion(cwd: string): string | undefined {
  const installedNextPackagePath = path.join(cwd, 'node_modules', 'next', 'package.json')
  if (fs.existsSync(installedNextPackagePath)) {
    try {
      const installedPkg = JSON.parse(fs.readFileSync(installedNextPackagePath, 'utf-8')) as {
        version?: unknown
      }
      if (typeof installedPkg.version === 'string' && installedPkg.version.length > 0) {
        return installedPkg.version
      }
    } catch {
      // Fall through to the project package.json dependency range.
    }
  }

  const projectPackagePath = path.join(cwd, 'package.json')
  if (!fs.existsSync(projectPackagePath)) return undefined

  try {
    const projectPkg = JSON.parse(fs.readFileSync(projectPackagePath, 'utf-8')) as {
      dependencies?: Record<string, unknown>
      devDependencies?: Record<string, unknown>
    }
    const nextVersion = projectPkg.dependencies?.next ?? projectPkg.devDependencies?.next
    if (typeof nextVersion !== 'string' || nextVersion.length === 0) {
      return undefined
    }
    return nextVersion.replace(/^[~^<>=\s]+/, '')
  } catch {
    return undefined
  }
}

function formatDevServerResult(result: DevScriptPortResult): string {
  switch (result.status) {
    case 'updated':
      return `set to ${result.port}`
    case 'preserved':
      return `using ${result.port}`
    case 'skipped':
      return `custom script preserved; BetterStart uses ${result.port}`
    case 'missing-script':
      return `no dev script; BetterStart uses ${result.port}`
    case 'missing-package':
      return `package.json missing; BetterStart uses ${result.port}`
  }
}

function shouldUseTerminalForDbPush(options: InitCommandOptions): boolean {
  return (
    !options.yes &&
    process.env.CI !== 'true' &&
    Boolean(process.stdin.isTTY) &&
    Boolean(process.stdout.isTTY)
  )
}

function betterstartExecCommand(
  pm: ReturnType<typeof detectPackageManager>,
  subcommand: string
): string {
  switch (pm) {
    case 'pnpm':
      return `pnpm betterstart ${subcommand}`.trim()
    case 'yarn':
      return `yarn betterstart ${subcommand}`.trim()
    case 'bun':
      return `bunx betterstart ${subcommand}`.trim()
    default:
      return `npx betterstart ${subcommand}`.trim()
  }
}

function drizzlePushCommand(pm: ReturnType<typeof detectPackageManager>): string {
  switch (pm) {
    case 'pnpm':
      return 'pnpm drizzle-kit push'
    case 'yarn':
      return 'yarn drizzle-kit push'
    case 'bun':
      return 'bunx drizzle-kit push'
    default:
      return 'npx drizzle-kit push'
  }
}

function formatAdminIdentity(admin: ExistingAdmin): string {
  return admin.name?.trim() ? `${admin.name} (${admin.email})` : admin.email
}

function isDatabaseReachabilityError(error: string): boolean {
  const normalized = error.toLowerCase()
  return [
    'admin account check timed out',
    'connect_timeout',
    'connection refused',
    'could not connect to database',
    'database connection failed',
    'database was not reachable',
    'econnrefused',
    'econnreset',
    'enotfound',
    'etimedout',
    'no route to host',
    'timeout',
    'timed out'
  ].some((pattern) => normalized.includes(pattern))
}

async function verifyDatabaseReachable(cwd: string): Promise<QuietCommandResult> {
  const databaseUrl = readExistingDbUrl(cwd)
  if (!databaseUrl) {
    return {
      success: false,
      error: 'Database URL is missing or invalid',
      output: ''
    }
  }

  const result = await runQuietCommand(
    process.execPath,
    [
      '-e',
      `
const { createRequire } = require('node:module')
const requireFromProject = createRequire(process.cwd() + '/package.json')
const { Client } = requireFromProject('pg')
const client = new Client({
  connectionString: process.env.DATABASE_URL,
  connectionTimeoutMillis: 10000
})

async function main() {
  try {
    await client.connect()
    await client.query('select 1')
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    console.error('Database connection failed: ' + message)
    process.exitCode = 1
  } finally {
    await client.end().catch(() => {})
  }
}

main().catch((error) => {
  const message = error instanceof Error ? error.message : String(error)
  console.error('Database connection failed: ' + message)
  process.exitCode = 1
})
`
    ],
    {
      cwd,
      timeoutMs: 15_000,
      env: {
        DATABASE_URL: databaseUrl
      }
    }
  )

  if (result.success) {
    return result
  }

  const error = parseSeedError(result.output, result.error)
  return {
    ...result,
    error
  }
}

function runSeedScript(
  cwd: string,
  adminDir: string,
  authBasePath: string,
  envOverrides: NodeJS.ProcessEnv
): Promise<{
  code: number | null
  stdout: string
  stderr: string
  timeout: boolean
  spawnError?: string
}> {
  const scriptsDir = path.join(cwd, adminDir, 'scripts')
  const seedPath = path.join(scriptsDir, 'seed.ts')

  if (!fs.existsSync(scriptsDir)) {
    fs.mkdirSync(scriptsDir, { recursive: true })
  }
  fs.writeFileSync(seedPath, buildSeedScript(authBasePath), 'utf-8')

  const cleanup = () => {
    try {
      fs.unlinkSync(seedPath)
      if (fs.existsSync(scriptsDir) && fs.readdirSync(scriptsDir).length === 0) {
        fs.rmdirSync(scriptsDir)
      }
    } catch {
      // Not critical
    }
  }

  return new Promise((resolve) => {
    const tsxBin = path.join(cwd, 'node_modules', '.bin', 'tsx')
    const child = spawn(tsxBin, [seedPath], {
      cwd,
      stdio: 'pipe',
      env: {
        ...process.env,
        ...envOverrides
      }
    })
    let stdout = ''
    let stderr = ''
    child.stdout?.on('data', (chunk: Buffer) => {
      stdout += chunk.toString()
    })
    child.stderr?.on('data', (chunk: Buffer) => {
      stderr += chunk.toString()
    })
    const timeout = setTimeout(() => {
      child.kill()
      cleanup()
      resolve({ code: null, stdout, stderr, timeout: true })
    }, 30_000)
    child.on('close', (code) => {
      clearTimeout(timeout)
      cleanup()
      resolve({ code, stdout, stderr, timeout: false })
    })
    child.on('error', (err) => {
      clearTimeout(timeout)
      cleanup()
      resolve({ code: null, stdout, stderr, timeout: false, spawnError: err.message })
    })
  })
}

/** Check whether an admin account already exists before prompting for credentials. */
async function checkExistingAdmin(
  cwd: string,
  adminDir: string,
  authBasePath: string
): Promise<ExistingAdminCheckResult> {
  const result = await runSeedScript(cwd, adminDir, authBasePath, {
    SEED_CHECK_ADMIN: 'true'
  })

  if (result.timeout) {
    return { error: 'Admin account check timed out after 30 seconds' }
  }

  if (result.spawnError) {
    return { error: parseSeedError(result.stdout, result.spawnError) }
  }

  if (result.code === 0) {
    return { error: null }
  }

  if (result.code === 3) {
    const rawAdmin = result.stdout.match(/EXISTING_ADMIN:(.+)/)?.[1]?.trim()
    if (!rawAdmin) {
      return { error: 'Admin account check returned an unreadable response' }
    }

    try {
      const existingAdmin = JSON.parse(rawAdmin) as ExistingAdmin
      return { existingAdmin, error: null }
    } catch {
      return { error: 'Admin account check returned an unreadable response' }
    }
  }

  return { error: parseSeedError(result.stdout, result.stderr) }
}

/** Write seed script, run it with tsx, clean up. Returns success/error. */
async function runSeed(
  cwd: string,
  adminDir: string,
  authBasePath: string,
  email: string,
  password: string,
  overwriteMode?: SeedOverwriteMode
): Promise<SeedResult> {
  const result = await runSeedScript(cwd, adminDir, authBasePath, {
    SEED_EMAIL: email,
    SEED_PASSWORD: password,
    SEED_NAME: 'Admin',
    ...(overwriteMode ? { SEED_OVERWRITE_MODE: overwriteMode } : {})
  })

  if (result.timeout) {
    return { success: false, error: 'Seed timed out after 30 seconds' }
  }

  if (result.spawnError) {
    return { success: false, error: parseSeedError(result.stdout, result.spawnError) }
  }

  if (result.code === 0) {
    return { success: true, error: null }
  }

  if (result.code === 2) {
    // Exit code 2 = user already exists
    const name = result.stdout.match(/EXISTING_USER:(.+)/)?.[1]?.trim()
    return { success: false, error: null, existingUser: name ?? email }
  }

  return { success: false, error: parseSeedError(result.stdout, result.stderr) }
}

/** Extract a human-readable error from seed script output */
function parseSeedError(stdout: string, stderr: string): string {
  // Check for common patterns in stderr/stdout
  const combined = `${stdout}\n${stderr}`

  // "Seed failed: <message>" from the catch handler
  const seedFailed = combined.match(/Seed failed:\s*(.+)/)?.[1]?.trim()
  if (seedFailed) return seedFailed

  // "Failed to create user" from auth API failure
  if (combined.includes('Failed to create user')) return 'Auth API failed to create user'

  // Connection errors
  if (isDatabaseReachabilityError(combined)) {
    return 'Could not connect to database'
  }
  if (combined.includes('DATABASE_URL')) return 'Database URL is missing or invalid'
  if (combined.includes('password authentication failed'))
    return 'Database authentication failed — check your connection string'
  if (combined.includes('does not exist') && combined.includes('relation'))
    return 'Database tables not found — run drizzle-kit push first'
  if (combined.includes('MODULE_NOT_FOUND') || combined.includes('Cannot find module'))
    return 'Missing dependencies — run your package manager install first'

  // Fall back to first meaningful line of stderr
  const firstLine = stderr
    .split('\n')
    .map((l) => l.trim())
    .find((l) => l.length > 0 && !l.startsWith('at ') && !l.startsWith('node:'))
  if (firstLine) return firstLine

  return 'Unknown error — run betterstart seed for details'
}

function runQuietCommand(
  bin: string,
  args: string[],
  options: {
    cwd: string
    timeoutMs: number
    env?: NodeJS.ProcessEnv
  }
): Promise<QuietCommandResult> {
  return new Promise((resolve) => {
    const child = spawn(bin, args, {
      cwd: options.cwd,
      stdio: 'pipe',
      env: {
        ...process.env,
        ...options.env
      }
    })

    let stdout = ''
    let stderr = ''
    let timedOut = false

    child.stdout?.on('data', (chunk: Buffer) => {
      stdout += chunk.toString()
    })

    child.stderr?.on('data', (chunk: Buffer) => {
      stderr += chunk.toString()
    })

    const timeout = setTimeout(() => {
      timedOut = true
      child.kill('SIGTERM')
    }, options.timeoutMs)

    child.on('close', (code, signal) => {
      clearTimeout(timeout)

      const output = [stdout.trimEnd(), stderr.trimEnd()].filter(Boolean).join('\n')

      if (timedOut) {
        resolve({
          success: false,
          error: `Command timed out after ${Math.floor(options.timeoutMs / 1000)} seconds`,
          output
        })
        return
      }

      if (signal) {
        resolve({
          success: false,
          error: `Command was terminated by ${signal}`,
          output
        })
        return
      }

      if (code === 0) {
        resolve({
          success: true,
          error: '',
          output
        })
        return
      }

      resolve({
        success: false,
        error: `Command exited with code ${code ?? 'unknown'}`,
        output
      })
    })

    child.on('error', (err) => {
      clearTimeout(timeout)
      const output = [stdout.trimEnd(), stderr.trimEnd()].filter(Boolean).join('\n')
      resolve({
        success: false,
        error: err.message,
        output
      })
    })
  })
}

function stripAnsi(value: string): string {
  return value.replace(ANSI_ESCAPE_PATTERN, '')
}

function printAdminReadyNote(state: {
  adminLoginUrl: string
  adminEmail?: string
  adminPassword?: string
}): void {
  const lines = [`Admin: ${pc.cyan(state.adminLoginUrl)}`]

  if (state.adminEmail && state.adminPassword) {
    lines.unshift(`Password: ${pc.cyan(state.adminPassword)}`)
    lines.unshift(`Admin user: ${pc.cyan(state.adminEmail)}`)
  }

  p.note(lines.join('\n'), 'Admin ready')
}

function shouldSuppressDevServerStartupLine(line: string): {
  suppress: boolean
  isReadyLine: boolean
} {
  const plain = stripAnsi(line).trim()

  if (plain.length === 0) {
    return { suppress: true, isReadyLine: false }
  }

  if (plain.includes('Ready in ')) {
    return { suppress: true, isReadyLine: true }
  }

  if (
    plain.startsWith('> ') ||
    plain.startsWith('▲ Next.js ') ||
    plain.startsWith('- ') ||
    plain.startsWith('· ') ||
    plain.startsWith('⨯ ')
  ) {
    return { suppress: true, isReadyLine: false }
  }

  if (plain.startsWith('✓ ') && !plain.includes('Ready in ')) {
    return { suppress: true, isReadyLine: false }
  }

  return { suppress: false, isReadyLine: false }
}

function pipeManagedDevServerStream(
  stream: NodeJS.ReadableStream | null | undefined,
  target: NodeJS.WriteStream,
  state: {
    suppressStartup: boolean
    readyLogged: boolean
    adminLoginUrl: string
    adminEmail?: string
    adminPassword?: string
  }
): void {
  if (!stream) return

  let buffer = ''

  const flushBuffer = (flushRemainder = false) => {
    let newlineIndex = buffer.indexOf('\n')

    while (newlineIndex !== -1) {
      const line = buffer.slice(0, newlineIndex + 1)
      buffer = buffer.slice(newlineIndex + 1)
      writeLine(line)
      newlineIndex = buffer.indexOf('\n')
    }

    if (flushRemainder && buffer.length > 0) {
      writeLine(buffer)
      buffer = ''
    }
  }

  const writeLine = (line: string) => {
    if (!state.suppressStartup) {
      target.write(line)
      return
    }

    const verdict = shouldSuppressDevServerStartupLine(line)

    if (verdict.isReadyLine) {
      if (!state.readyLogged) {
        printAdminReadyNote(state)
        state.readyLogged = true
      }
      return
    }

    if (verdict.suppress) {
      return
    }

    if (state.readyLogged) {
      state.suppressStartup = false
    }

    target.write(line)
  }

  stream.on('data', (chunk: Buffer | string) => {
    buffer += chunk.toString()
    flushBuffer()
  })

  stream.on('end', () => {
    flushBuffer(true)
  })
}

function startManagedDevServer(
  cwd: string,
  devCmd: string,
  adminLoginUrl: string,
  adminCredentials?: {
    email?: string
    password?: string
  }
): Promise<void> {
  return new Promise((resolve, reject) => {
    const [bin, ...args] = devCmd.split(' ')
    const child = spawn(bin, args, {
      cwd,
      stdio: ['inherit', 'pipe', 'pipe']
    })

    const state = {
      suppressStartup: true,
      readyLogged: false,
      adminLoginUrl,
      adminEmail: adminCredentials?.email,
      adminPassword: adminCredentials?.password
    }

    const forwardSignal = (signal: NodeJS.Signals) => {
      if (!child.killed) {
        child.kill(signal)
      }
    }

    const cleanup = () => {
      process.off('SIGINT', forwardSignal)
      process.off('SIGTERM', forwardSignal)
      process.off('SIGHUP', forwardSignal)
    }

    process.on('SIGINT', forwardSignal)
    process.on('SIGTERM', forwardSignal)
    process.on('SIGHUP', forwardSignal)

    pipeManagedDevServerStream(child.stdout, process.stdout, state)
    pipeManagedDevServerStream(child.stderr, process.stderr, state)

    child.on('close', (code, signal) => {
      cleanup()

      if (signal) {
        process.kill(process.pid, signal)
        return
      }

      if (typeof code === 'number' && code !== 0) {
        process.exitCode = code
      }

      resolve()
    })

    child.on('error', (err) => {
      cleanup()
      reject(err)
    })
  })
}
